<script setup lang="ts">
interface AttractionCard {
  slug: string;
  label: string;
  category: "nature" | "city" | "history" | "transport" | "beach";
  image: string;
  image_alt?: string;
  km: number;
  minutes: number;
  mode: "walk" | "taxi" | "bus" | "drive";
  short_description: string;
  full_description: string[];
  lat?: number;
  lng?: number;
  season_hint?: string;
  alternate_names?: string[];
  paid?: boolean;
}

interface AttractionsGalleryBlock {
  title: string;
  intro?: string;
  items: AttractionCard[];
}

const props = defineProps<{
  block: AttractionsGalleryBlock;
  activeIndex?: number | null;
}>();

const emit = defineEmits<{
  (e: "card:hover", index: number | null): void;
  (e: "card:focus", index: number): void;
}>();

const { t, n } = useI18n();
const localePath = useLocalePath();

interface LinkifyRule {
  re: RegExp;
  href: string;
  external?: boolean;
}

const linkRules = computed<LinkifyRule[]>(() => [
  { re: /\b(Argo|Арго|არგო)\b/g, href: localePath("/amenities") },
  {
    re: /\b(аквапарк[а-я]*|aquapark|აკვაპარკი)\b/giu,
    href: localePath("/amenities"),
  },
  {
    re: /\b(Stadler)\b/g,
    href: "https://tkt.ge/en/railway",
    external: true,
  },
]);

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function linkify(text: string): string {
  let out = escapeHtml(text);
  for (const rule of linkRules.value) {
    const attrs = rule.external
      ? `target="_blank" rel="noopener"`
      : "";
    out = out.replace(
      rule.re,
      (m) =>
        `<a href="${rule.href}" class="font-medium text-amber-600 underline decoration-amber-400/40 underline-offset-2 transition-colors hover:text-amber-500 hover:decoration-amber-400 dark:text-amber-400 dark:hover:text-amber-300" ${attrs}>${m}</a>`
    );
  }
  return out;
}

const openIndex = ref<number | null>(null);
const openCard = computed(() =>
  openIndex.value != null ? props.block.items[openIndex.value] : null
);

function openCardAt(i: number) {
  openIndex.value = i;
  if (import.meta.client) document.body.style.overflow = "hidden";
}

function closeCard() {
  openIndex.value = null;
  if (import.meta.client) document.body.style.overflow = "";
}

function nextCard() {
  if (openIndex.value == null) return;
  openIndex.value = (openIndex.value + 1) % props.block.items.length;
}

function prevCard() {
  if (openIndex.value == null) return;
  openIndex.value =
    (openIndex.value - 1 + props.block.items.length) % props.block.items.length;
}

function focusOnMap(i: number) {
  emit("card:focus", i);
  closeCard();
}

function routeHref(card: AttractionCard) {
  if (card.lat == null || card.lng == null) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${card.lat},${card.lng}`;
}

function onKeydown(e: KeyboardEvent) {
  if (openIndex.value == null) return;
  if (e.key === "Escape") closeCard();
  if (e.key === "ArrowRight") nextCard();
  if (e.key === "ArrowLeft") prevCard();
}

const formatKm = (km: number) => (km < 1 ? km.toFixed(1) : n(km));

onMounted(() => window.addEventListener("keydown", onKeydown));
onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown);
  if (import.meta.client) document.body.style.overflow = "";
});
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-lime-50/30 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
    aria-labelledby="attractions-gallery-heading"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-amber-400/10 blur-3xl dark:bg-amber-400/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -right-16 h-80 w-80 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
    />

    <div class="relative mx-auto w-full max-w-7xl px-4 py-8 md:py-12">
      <header data-reveal class="mb-6 max-w-3xl md:mb-8">
        <div
          class="flex items-center gap-3 font-body text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500"
        >
          <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
          <span>{{ t("location.gallery.eyebrow") }}</span>
        </div>
        <h2
          id="attractions-gallery-heading"
          class="mt-4 font-display text-3xl italic leading-tight text-slate-900 md:text-5xl dark:text-white"
        >
          {{ block.title }}
        </h2>
        <p
          v-if="block.intro"
          class="mt-5 font-body text-base text-slate-600 md:text-lg dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
      </header>

      <ul
        data-reveal
        class="grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-7 lg:grid-cols-3"
      >
        <li
          v-for="(item, i) in block.items"
          :id="item.slug"
          :key="i"
          class="group relative flex flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-slate-900/5 shadow-lg shadow-slate-900/5 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:ring-amber-400/30 dark:bg-slate-900 dark:ring-white/10 scroll-mt-24"
          :class="{
            '!ring-amber-400 shadow-xl -translate-y-1': activeIndex === i,
          }"
          @mouseenter="emit('card:hover', i)"
          @mouseleave="emit('card:hover', null)"
        >
          <button
            type="button"
            class="relative block aspect-[16/10] w-full overflow-hidden bg-slate-200 dark:bg-slate-800"
            :aria-label="t('location.gallery.open', { place: item.label })"
            @click="openCardAt(i)"
          >
            <NuxtImg
              :src="item.image"
              :alt="item.image_alt || item.label"
              width="800"
              height="500"
              format="webp"
              loading="lazy"
              sizes="xs:100vw sm:50vw md:33vw lg:33vw"
              class="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            />
            <div
              aria-hidden="true"
              class="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-slate-900/10 to-transparent"
            />

            <span
              class="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 font-body text-[10px] uppercase tracking-[0.2em] text-slate-700 backdrop-blur-sm dark:bg-slate-900/80 dark:text-slate-200"
            >
              <span
                aria-hidden="true"
                class="inline-block h-1 w-1 rounded-full bg-amber-400"
              />
              {{ t(`location.gallery.category.${item.category}`) }}
            </span>

            <span
              class="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-slate-900/80 px-2.5 py-1 font-body text-[11px] text-white backdrop-blur-sm"
            >
              <svg
                v-if="item.mode === 'walk'"
                viewBox="0 0 24 24"
                class="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="13" cy="4" r="2" />
                <path
                  d="M9.5 22l1.8-5.5L8 13l1-5 3 1 2 3 3 1M7 10l3-1 3 3 2 6"
                />
              </svg>
              <svg
                v-else-if="item.mode === 'bus'"
                viewBox="0 0 24 24"
                class="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="4" y="5" width="16" height="12" rx="2" />
                <path d="M4 11h16M8 17v2M16 17v2" />
                <circle cx="8" cy="14" r="1" />
                <circle cx="16" cy="14" r="1" />
              </svg>
              <svg
                v-else
                viewBox="0 0 24 24"
                class="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M5 15l1.6-5.2a2 2 0 012-1.3h6.8a2 2 0 012 1.3L19 15" />
                <rect x="3" y="14" width="18" height="4" rx="1" />
                <circle cx="7.5" cy="18.5" r="1.2" />
                <circle cx="16.5" cy="18.5" r="1.2" />
              </svg>
              <span class="tabular-nums">
                {{ item.minutes }} {{ t("location.distances.units.min") }} ·
                {{ formatKm(item.km) }} {{ t("location.distances.units.km") }}
              </span>
            </span>
          </button>

          <div class="flex flex-1 flex-col p-5 md:p-6">
            <h3
              class="font-display text-xl italic leading-tight text-slate-900 md:text-2xl dark:text-white"
            >
              {{ item.label }}
            </h3>
            <div class="mt-2 h-px w-10 bg-amber-400" aria-hidden="true" />
            <p
              class="mt-3 line-clamp-3 font-body text-sm leading-relaxed text-slate-600 dark:text-slate-300"
              v-html="linkify(item.short_description)"
            />

            <div
              class="mt-5 flex items-center justify-between border-t border-slate-200/80 pt-4 dark:border-slate-700/60"
            >
              <button
                type="button"
                class="inline-flex items-center gap-1.5 font-body text-xs uppercase tracking-[0.2em] text-amber-600 transition-colors hover:text-amber-500 dark:text-amber-400 dark:hover:text-amber-300"
                @click="openCardAt(i)"
              >
                {{ t("location.gallery.more") }}
                <span aria-hidden="true" class="transition-transform group-hover:translate-x-1">→</span>
              </button>
              <button
                v-if="item.lat != null && item.lng != null"
                type="button"
                class="inline-flex items-center gap-1 font-body text-xs text-slate-500 transition-colors hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                :title="t('location.gallery.see_on_map')"
                @click="focusOnMap(i)"
              >
                <Icon name="tabler:map-pin" size="14" aria-hidden="true" />
                <span class="hidden sm:inline">{{ t("location.gallery.see_on_map") }}</span>
              </button>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0"
        leave-active-class="transition duration-200 ease-in"
        leave-to-class="opacity-0"
      >
        <div
          v-if="openCard"
          class="fixed inset-0 z-[9999] flex items-end justify-center bg-slate-900/90 backdrop-blur-md md:items-center"
          role="dialog"
          aria-modal="true"
          :aria-label="openCard.label"
          @click.self="closeCard"
        >
          <Transition
            appear
            enter-active-class="transition duration-400 ease-out"
            enter-from-class="opacity-0 translate-y-6 md:translate-y-0 md:scale-95"
            enter-to-class="opacity-100 translate-y-0 md:scale-100"
          >
            <article
              :key="openIndex ?? -1"
              class="relative flex h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl md:h-auto md:max-h-[88vh] md:rounded-3xl dark:bg-slate-900"
            >
              <button
                type="button"
                class="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow-md backdrop-blur-sm transition-all hover:bg-white hover:text-slate-900 dark:bg-slate-900/90 dark:text-slate-200 dark:hover:bg-slate-900 dark:hover:text-white"
                :aria-label="t('lightbox.close')"
                @click="closeCard"
              >
                <Icon name="mdi:close" size="22" />
              </button>

              <div class="relative aspect-[16/10] w-full shrink-0 overflow-hidden bg-slate-200 md:aspect-[21/9] dark:bg-slate-800">
                <NuxtImg
                  :src="openCard.image"
                  :alt="openCard.image_alt || openCard.label"
                  width="1600"
                  height="900"
                  format="webp"
                  sizes="xs:100vw sm:100vw md:900px"
                  class="absolute inset-0 h-full w-full object-cover"
                />
                <div
                  aria-hidden="true"
                  class="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent"
                />
                <div class="absolute inset-x-0 bottom-0 p-6 md:p-10">
                  <div
                    class="flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.22em] text-amber-300"
                  >
                    <span
                      aria-hidden="true"
                      class="inline-block h-px w-8 bg-amber-400"
                    />
                    {{ t(`location.gallery.category.${openCard.category}`) }}
                    <span aria-hidden="true" class="opacity-40">·</span>
                    <span class="tabular-nums">
                      {{ openCard.minutes }} {{ t("location.distances.units.min") }}
                      · {{ formatKm(openCard.km) }}
                      {{ t("location.distances.units.km") }}
                    </span>
                  </div>
                  <h3
                    class="mt-2 font-display text-3xl italic leading-tight text-white drop-shadow md:text-5xl"
                  >
                    {{ openCard.label }}
                  </h3>
                </div>
              </div>

              <div class="flex-1 overflow-y-auto px-6 py-8 md:px-10 md:py-10">
                <div class="prose prose-slate max-w-none dark:prose-invert">
                  <p
                    v-for="(paragraph, pi) in openCard.full_description"
                    :key="pi"
                    class="font-body text-base leading-relaxed text-slate-700 md:text-lg dark:text-slate-200"
                    v-html="linkify(paragraph)"
                  />
                </div>

                <div
                  v-if="openCard.season_hint"
                  class="mt-6 flex items-start gap-3 rounded-xl border border-amber-400/30 bg-amber-400/5 p-4 font-body text-sm text-slate-700 dark:text-slate-200"
                >
                  <Icon
                    name="tabler:sun"
                    size="20"
                    class="mt-0.5 shrink-0 text-amber-500"
                    aria-hidden="true"
                  />
                  <span>{{ openCard.season_hint }}</span>
                </div>
              </div>

              <footer
                class="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 md:px-10 dark:border-slate-700 dark:bg-slate-800/60"
              >
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white"
                    :aria-label="t('lightbox.prev')"
                    @click="prevCard"
                  >
                    <Icon name="mdi:chevron-left" size="22" />
                  </button>
                  <button
                    type="button"
                    class="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white"
                    :aria-label="t('lightbox.next')"
                    @click="nextCard"
                  >
                    <Icon name="mdi:chevron-right" size="22" />
                  </button>
                </div>

                <div class="flex gap-3">
                  <button
                    v-if="openCard.lat != null"
                    type="button"
                    class="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 font-body text-xs uppercase tracking-[0.2em] text-slate-700 transition-colors hover:border-amber-400 hover:text-amber-600 dark:border-slate-600 dark:text-slate-200 dark:hover:text-amber-400"
                    @click="openIndex != null && focusOnMap(openIndex)"
                  >
                    <Icon name="tabler:map-pin" size="14" />
                    {{ t("location.gallery.see_on_map") }}
                  </button>
                  <a
                    v-if="routeHref(openCard)"
                    :href="routeHref(openCard)!"
                    target="_blank"
                    rel="noopener"
                    class="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-1.5 font-body text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-amber-500 dark:bg-white dark:text-slate-900 dark:hover:bg-amber-400"
                  >
                    <Icon name="tabler:route" size="14" />
                    {{ t("location.gallery.route") }}
                  </a>
                </div>
              </footer>
            </article>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>
